import { memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faOctagonExclamation } from '@fortawesome/pro-regular-svg-icons/faOctagonExclamation';
import { Divider as HeroUIDivider } from '@heroui/react';
import { Button } from 'Button';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import { type LearnerProject } from 'LearnerProjects';
import { Trans, useTranslation } from 'react-i18next';
import { getCohort } from 'Users';
import './locales/confirm_project_group_modal_page-en.json';
import { useCurrentUser } from 'FrontRoyalAngular';
import { getBrandName } from 'AppBranding';
import { projectGroupsApi } from './projectGroupsApi';
import { MembersList } from './MembersList';
import { type MemberDetails, MemberDetailsMembershipStatus } from './ProjectGroup.types';

const Description = ({ project }: { project: LearnerProject }) => {
    const { t } = useTranslation('back_royal');
    const currentUser = useCurrentUser();

    return (
        <Text color="black">
            <Trans
                i18nKey="confirm_project_group_modal_page.description"
                t={t}
                components={{
                    strong: <span className="font-bold" />,
                }}
                values={{
                    projectTitle: project.title,
                    maxGroupSize: project.maxGroupSize,
                    institutionName: getBrandName(currentUser),
                }}
            />
        </Text>
    );
};

const Warning = ({ className }: { className?: string }) => {
    const { t } = useTranslation('back_royal');
    return (
        <div
            className={twMerge(
                'flex items-start justify-between gap-3 rounded-lg bg-primary-100 px-4 py-3 text-primary',
                className,
            )}
        >
            <div className="flex items-center gap-3">
                <div className="rounded-full bg-white p-1.5 shadow-smallish">
                    <FontAwesomeIcon icon={faOctagonExclamation} size="lg" />
                </div>
                <div className="flex-1 text-primary-600">
                    <Trans
                        i18nKey="confirm_project_group_modal_page.warning"
                        t={t}
                        components={{
                            strong: <p className="font-medium leading-5" />,
                            'semi-strong': <p className="leading-5" />,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const Divider = ({ members, className }: { members: MemberDetails[]; className?: string }) => {
    const { t } = useTranslation('back_royal');
    return (
        <div className={twMerge('flex items-center justify-center gap-4', className)}>
            <HeroUIDivider className="flex-1" />
            <Text variant="h2">{t('confirm_project_group_modal_page.divider', { numMembers: members.length })}</Text>
            <HeroUIDivider className="flex-1" />
        </div>
    );
};

const ConfirmationButtons = ({
    className,
    members,
    project,
    onCancel,
    onConfirm,
}: {
    className?: string;
    members: MemberDetails[];
    project: LearnerProject;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const { t } = useTranslation('back_royal');
    const currentUser = useCurrentUser();
    const [submit, { isLoading }] = projectGroupsApi.useCreateMutation();
    const createProjectGroup = async () => {
        const response = await submit({
            record: {
                member_emails: members.map(member => member.email),
                cohort_id: getCohort(currentUser)!.id,
                requirement_identifier: project.requirementIdentifier,
            },
        });
        if (response.error) {
            return;
        }
        onConfirm();
    };

    return (
        <div className={twMerge('flex flex-col gap-3 xs:flex-row xs:justify-center xs:gap-7.5', className)}>
            <Button className="font-normal" variant="light" onPress={onCancel}>
                {t('confirm_project_group_modal_page.cancel_button_label')}
            </Button>
            <Button variant="solid" color="primary" isDisabled={isLoading} onPress={createProjectGroup}>
                {isLoading
                    ? t('confirm_project_group_modal_page.confirm_button_loading_label')
                    : t('confirm_project_group_modal_page.confirm_button_label')}
            </Button>
        </div>
    );
};

const ConfirmProjectGroupModalPageComponent = ({
    project,
    members,
    onConfirm,
    onCancel,
}: {
    project: LearnerProject;
    members: MemberDetails[];
    onConfirm: () => void;
    onCancel: () => void;
}) => (
    <div className="flex h-full flex-col">
        <Description project={project} />
        <Warning className="my-7.5" />
        <Divider className="mb-7.5" members={members} />
        <MembersList
            members={members}
            showMemberStatus={member => member.detailsStatus === MemberDetailsMembershipStatus.CREATOR}
        />
        <ConfirmationButtons
            className="mt-12.5"
            members={members}
            project={project}
            onCancel={onCancel}
            onConfirm={onConfirm}
        />
    </div>
);

export const ConfirmProjectGroupModalPage = memo(ConfirmProjectGroupModalPageComponent);
